import { useMemo, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import type { OpcaoProjecao, PreviaProjecaoTransferencia } from "@/modules/financeiro-obras";
import { useConfirmarDescarte } from "@/shared/hooks";
import { useOpcoesProjecao } from "../../../../hooks/use-opcoes-projecao";
import { NO_VALUE } from "../planejamento-controle.constants";
import { formatMil, formatMoeda, formatPercentual } from "../planejamento-controle.format";
import {
  decisaoDoRascunho,
  gravadasDaOrigem,
  percentualValido,
  rascunhoInicial,
} from "../projecao-transferencia";
import type { RascunhoAjuste, RascunhoCategoria } from "../projecao-transferencia";
import {
  AJUSTAR_FORM_ID,
  AJUSTAR_TITLE,
  CURVA_LABEL,
  DISTRIBUICAO_LABEL,
  FATOR_ATUALIZAR,
  FATOR_ATUALIZAR_MANUAL,
  FATOR_ATUALIZAR_SEM_VALOR,
  FATOR_CAMPO_LABEL,
  FATOR_DIFERENCA,
  FATOR_FORA_DA_FAIXA,
  FATOR_MANTER,
  FATOR_NAO_SE_APLICA,
  FATOR_OBRIGATORIO,
  FATOR_RECALCULADO,
  FATOR_SEM_DIFERENCA,
  FATOR_SEM_IDADE_TEXTO,
  FATOR_SUBTITULO_ATUALIZAR,
  FATOR_SUBTITULO_MANTER,
  FATOR_TEXTO,
  FATOR_TITLE,
  MATERIAIS_TITLE,
  NA_ORIGEM,
  PREVIA_ERRO,
  PREVIA_PERMISSAO,
  RESUMO_COLUNAS,
  RESUMO_TITLE,
  REVISAR_TITLE,
  SEM_NOME_NA_ORIGEM,
  SERVICOS_TITLE,
  TOTAL_NO_DESTINO_LABEL,
  VALOR_ESCOPO,
} from "./ajustar-projecao-transferencia-dialog.constants";
import type { AjustarProjecaoTransferenciaDialogProps } from "./types-ajustar-projecao-transferencia-dialog";

type Chave = "servicos" | "materiais";
const reais = (valor: number) => formatMoeda(valor, "BRL");
const paraOpcoes = (lista: OpcaoProjecao[]) => lista.map((o) => ({ label: o.nome, value: o.id }));
const percentualTexto = (valor: number) =>
  `${valor.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
const textoDaDiferenca = (anterior: number, recalculado: number) => {
  const diferenca = recalculado - anterior;
  if (diferenca === 0) return FATOR_SEM_DIFERENCA;
  return FATOR_DIFERENCA(diferenca > 0 ? "+" : "-", reais(Math.abs(diferenca)));
};
const celulasVazias = RESUMO_COLUNAS.slice(1).map(() => NO_VALUE);

export function useAjustarProjecaoTransferenciaDialog({
  variante,
  chavePrevia,
  buscarPrevia,
  cartao,
  origem,
  decisao,
  onAplicar,
  onClose,
}: AjustarProjecaoTransferenciaDialogProps) {
  const gravadas = useMemo(() => gravadasDaOrigem(origem), [origem]);
  const { status: statusOpcoes, opcoes } = useOpcoesProjecao(gravadas);
  const [rascunho, setRascunho] = useState<RascunhoAjuste>(() => rascunhoInicial(origem, decisao));
  const decisaoAtual = decisaoDoRascunho(origem, rascunho);
  const alterado = JSON.stringify(decisaoAtual) !== JSON.stringify(decisao);
  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "ajustar-descarte" });

  const previa = useAsyncQuery<PreviaProjecaoTransferencia | null>({
    fetcher: (signal) => buscarPrevia(decisaoAtual, { signal }),
    queryKey: `ajustar-previa/${chavePrevia}/${JSON.stringify(decisaoAtual)}`,
    initialData: null,
    keepPrevious: true,
    forbiddenMessage: PREVIA_PERMISSAO,
    errorMessage: PREVIA_ERRO,
    resolveErrorMessage: getErrorMessage,
  });
  const resultado = previa.data?.projecao ?? null;

  const alterarCategoria = (chave: Chave, campo: keyof RascunhoCategoria, valor: string | null) =>
    setRascunho((atual) => {
      const categoria = atual[chave];
      return categoria ? { ...atual, [chave]: { ...categoria, [campo]: valor } } : atual;
    });

  const bloco = (chave: Chave) => {
    const categoria = rascunho[chave];
    const daOrigem = origem[chave];
    if (!categoria || !daOrigem) return null;
    const categoriaProjecao = chave === "servicos" ? "SERVICOS" : "MATERIAIS";
    const titulo = chave === "servicos" ? SERVICOS_TITLE : MATERIAIS_TITLE;
    const id = `ajustar-${chave}`;
    return {
      chave,
      titulo,
      ids: {
        cartao: id,
        distribuicao: `${id}-distribuicao`,
        distribuicaoOrigem: `${id}-distribuicao-origem`,
        curva: `${id}-curva`,
        curvaOrigem: `${id}-curva-origem`,
      },
      distribuicaoLabel: DISTRIBUICAO_LABEL(titulo),
      curvaLabel: CURVA_LABEL(titulo),
      distribuicaoId: categoria.distribuicaoId,
      curvaId: categoria.curvaId,
      distribuicoes: paraOpcoes(opcoes.distribuicoes[categoriaProjecao]),
      curvas: paraOpcoes(opcoes.curvas[categoriaProjecao]),
      naOrigemDistribuicao: NA_ORIGEM(daOrigem.distribuicao?.nome ?? SEM_NOME_NA_ORIGEM),
      naOrigemCurva: NA_ORIGEM(daOrigem.curva?.nome ?? SEM_NOME_NA_ORIGEM),
      escolherDistribuicao: (id: string | null) => alterarCategoria(chave, "distribuicaoId", id),
      escolherCurva: (id: string | null) => alterarCategoria(chave, "curvaId", id),
    };
  };

  const fator = resultado?.fator ?? null;
  const semIdade = fator?.semIdade === true;
  const atualizando = rascunho.fator === "ATUALIZAR";
  const percentualMostrado = rascunho.fatorPercentual ?? fator?.atualizado ?? null;
  let erroPercentual: string | null = null;
  if (atualizando) {
    if (rascunho.fatorPercentual !== null && !percentualValido(rascunho.fatorPercentual)) {
      erroPercentual = FATOR_FORA_DA_FAIXA;
    } else if (rascunho.fatorPercentual === null && semIdade) {
      erroPercentual = FATOR_OBRIGATORIO;
    }
  }
  let rotuloAtualizar = FATOR_ATUALIZAR_SEM_VALOR;
  if (percentualValido(percentualMostrado)) {
    rotuloAtualizar = FATOR_ATUALIZAR(percentualTexto(percentualMostrado));
  } else if (semIdade) {
    rotuloAtualizar = FATOR_ATUALIZAR_MANUAL;
  }

  const meses = resultado?.competencias ?? [];
  const linhaMensal = (
    nome: string,
    categoria: {
      mensalizacao: { competencia: string; valor: number }[];
      valorReais: number | null;
    } | null,
  ) =>
    categoria
      ? {
          nome,
          testId: `ajustar-mensal-${nome}`,
          valores: meses.map((competencia) => {
            const mes = categoria.mensalizacao.find((m) => m.competencia === competencia);
            return mes ? formatMil(mes.valor) : formatMil(null);
          }),
          total: formatMil(categoria.valorReais),
        }
      : null;

  return {
    formId: AJUSTAR_FORM_ID,
    titulo: variante === "tipo" ? REVISAR_TITLE : AJUSTAR_TITLE,
    cartao:
      variante === "tipo"
        ? {
            ...cartao,
            projecaoLabel: TOTAL_NO_DESTINO_LABEL,
            projecao: resultado ? reais(resultado.totalDestino) : null,
          }
        : cartao,
    resumo:
      variante === "tipo"
        ? {
            titulo: RESUMO_TITLE,
            colunas: [...RESUMO_COLUNAS],
            linhas: [
              { tipo: SERVICOS_TITLE, celulas: celulasVazias },
              ...(origem.materiais ? [{ tipo: MATERIAIS_TITLE, celulas: celulasVazias }] : []),
            ],
          }
        : null,
    carregandoOpcoes: statusOpcoes === "loading",
    blocos: [bloco("servicos"), bloco("materiais")].filter((b) => b !== null),
    fatorNaoSeAplica:
      variante === "tipo" && cartao.tipoObraCodigo === "100" && !origem.usaCustoPadrao
        ? FATOR_NAO_SE_APLICA
        : null,
    fator: origem.usaCustoPadrao
      ? {
          titulo: FATOR_TITLE,
          texto: FATOR_TEXTO,
          subtitulo: atualizando ? FATOR_SUBTITULO_ATUALIZAR : FATOR_SUBTITULO_MANTER,
          selecionado: rascunho.fator,
          opcoes: [
            {
              valor: "MANTER" as const,
              testId: "ajustar-fator-manter",
              label: FATOR_MANTER(formatPercentual(origem.fatorIdade)),
            },
            {
              valor: "ATUALIZAR" as const,
              testId: "ajustar-fator-atualizar",
              label: rotuloAtualizar,
            },
          ],
          valorEscopo:
            !atualizando && resultado
              ? VALOR_ESCOPO(
                  reais(resultado.totalDestino),
                  resultado.totalDestino === resultado.totalOrigem,
                  reais(resultado.totalOrigem),
                )
              : null,
          recalculado:
            atualizando && fator && fator.valorRecalculado !== null
              ? FATOR_RECALCULADO(
                  reais(fator.valorAnterior),
                  reais(fator.valorRecalculado),
                  textoDaDiferenca(fator.valorAnterior, fator.valorRecalculado),
                )
              : null,
          campo: atualizando
            ? {
                label: FATOR_CAMPO_LABEL,
                valor: percentualMostrado,
                erro: erroPercentual,
                aviso: semIdade ? FATOR_SEM_IDADE_TEXTO : null,
                digitar: (valor: number | null) =>
                  setRascunho((atual) => ({ ...atual, fatorPercentual: valor })),
              }
            : null,
          escolher: (valor: "MANTER" | "ATUALIZAR") =>
            setRascunho((atual) => ({
              ...atual,
              fator: valor,
              fatorPercentual: valor === "ATUALIZAR" ? atual.fatorPercentual : null,
            })),
        }
      : null,
    mensalizacao: {
      competencias: meses,
      linhas: [
        linhaMensal(SERVICOS_TITLE, resultado?.servicos ?? null),
        linhaMensal(MATERIAIS_TITLE, resultado?.materiais ?? null),
      ].filter((linha) => linha !== null),
      calculando: previa.status === "loading",
    },
    erro: previa.status === "error" || previa.status === "forbidden" ? previa.errorMessage : null,
    recarregar: previa.reload,
    aplicarDesabilitado:
      previa.status !== "success" || statusOpcoes !== "success" || erroPercentual !== null,
    aplicar: () => onAplicar(decisaoAtual),
    cancelar: () => descarte.pedir(alterado),
    descarte: descarte.confirmacao,
  };
}
